import React, { useState } from 'react';
import { TagIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { TagList, TagViewToggle, groupTagsByCategory, type TagDisplayMode } from '../Content/TagDisplay';
import type { SubgenreContentTag } from '../../data/subgenreContentTags';

/**
 * Human-readable framing for each tag category in the context of a subgenre.
 * Grouping matters here: on Sports Romance, "Hockey" answers *which sport* and
 * "Rivals" answers *what dynamic* — independent choices that combine. A flat
 * list hides that; these headings make the AND-combining behaviour obvious.
 */
const CATEGORY_HINTS: Record<string, string> = {
  Settings: 'Where it happens',
  Characters: 'Who it happens to',
  Tropes: 'What drives it',
  Themes: 'What it explores',
  'Content Warnings': 'What to know first',
  'Relationship Types': 'How many, and who'
};
const CATEGORY_ORDER = ['Settings', 'Characters', 'Tropes', 'Relationship Types', 'Themes', 'Content Warnings'];
interface PopularContentTagsProps {
  tags: SubgenreContentTag[];
  selectedTagIds: string[];
  onToggleTag: (tagId: string) => void;
  onClearTags: () => void;
  /** Book counts within the current subgenre, keyed by tag id. */
  resultCounts?: Record<string, number>;
  subgenreName: string;
}
export const PopularContentTags: React.FC<PopularContentTagsProps> = ({
  tags,
  selectedTagIds,
  onToggleTag,
  onClearTags,
  resultCounts,
  subgenreName
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [mode, setMode] = useState<TagDisplayMode>('cards');
  if (tags.length === 0) return null;
  const groups = groupTagsByCategory(tags).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a.category);
    const bi = CATEGORY_ORDER.indexOf(b.category);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  const countFor = (tag: {
    id: string;
    count?: number;
  }) => resultCounts ? resultCounts[tag.id] ?? 0 : tag.count;
  return <section aria-labelledby="popular-content-tags" className="mb-10" data-id="element-1617" data-mp="23m5vdyu0q567">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-id="element-1618" data-mp="1zlc5izx0mgao">
        <div className="flex items-center gap-2" data-id="element-1619" data-mp="2dc8ywsyrn1is">
          <TagIcon className={`h-5 w-5 ${rt.textPrimary}`} data-id="element-1620" data-mp="2dd0j77mwxctr" />
          <h2 id="popular-content-tags" className={`text-lg font-bold ${dk.textHeading}`} data-id="element-1621" data-mp="28kqz488hb6ze">
            Popular Content Tags
          </h2>
          <span className={`text-sm ${dk.textMuted}`} data-id="element-1622" data-mp="2ib2b8i2bgmhd">in {subgenreName}</span>
        </div>
        <div className="flex items-center gap-3" data-id="element-1623" data-mp="2wal5q827fzb7">
          {selectedTagIds.length > 0 && <button onClick={onClearTags} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-1624" data-mp="2rbgz9p6o0fok">
              Clear {selectedTagIds.length}
            </button>}
          <TagViewToggle mode={mode} setMode={setMode} data-id="element-1625" data-mp="wxpj06ttyxk5" />
        </div>
      </div>

      {/* Grouped so readers see the independent axes they can combine */}
      <div className={mode === 'compact' ? 'space-y-4' : 'space-y-6'} data-id="element-1626" data-mp="38lkw2q45lk0n">
        {groups.map(group => <div key={group.category} data-id="element-1627" data-mp="3dm089ecp8lh1">
            <div className="flex items-baseline gap-2 mb-2" data-id="element-1628" data-mp="2zsr53vfcgw6y">
              <h3 className={`text-xs font-semibold uppercase tracking-wide ${dk.textMuted}`} data-id="element-1629" data-mp="2zqenxkheupuy">
                {group.category}
              </h3>
              {CATEGORY_HINTS[group.category] && <span className={`text-xs ${dk.textMuted}`} data-id="element-1630" data-mp="2ut4qpins59wk">
                  · {CATEGORY_HINTS[group.category]}
                </span>}
            </div>
            <TagList tags={group.items} mode={mode} selectedIds={selectedTagIds} onToggle={onToggleTag} countFor={countFor} data-id="element-1631" data-mp="2gvpx01qhufys" />
          </div>)}
      </div>
    </section>;
};
export default PopularContentTags;